import { Lock } from "@tamagui/lucide-icons";
import React from "react";
import { Platform, TextInput } from "react-native";
import {
  UseMaskedInputProps,
  useMaskedInputProps,
} from "react-native-mask-input";
import {
  GetProps,
  Input as TamaguiInput,
  TextArea as TamaguiTextArea,
  XStack,
  YStack,
  styled,
  withStaticProperties,
} from "tamagui";

import FormField from "./FormField";
import { useFormFieldAria } from "./useFormFieldAria";
import { useFormFieldIds } from "./useFormFieldIds";

const InputFrame = styled(YStack, {
  name: "Input",
  backgroundColor: "$fillInput",
  borderWidth: "$strokeWidth",
  borderColor: "$strokeNeutral",
  borderRadius: "$150",
  paddingHorizontal: "$150",
  paddingVertical: "$150",
  justifyContent: "center",
  minHeight: 48,
  variants: {
    focused: {
      true: {
        outlineColor: "$outlineColor",
        outlineWidth: 2,
        outlineStyle: "solid",
        outlineOffset: 2,
      },
    },
    readonly: {
      true: {
        backgroundColor: "$fillNeutral",
        borderColor: "$strokeSubdued",
        color: "$textSecondary",
      },
    },
    error: {
      true: {
        borderColor: "$red",
        backgroundColor: "$fillInput",
      },
    },
  } as const,
});

type InputProps = {
  mode?: "bordered";
  prefix?: React.ReactNode;
  postfix?: React.ReactNode;
  label?: string;
  rootProps?: GetProps<typeof YStack>;
  frameProps?: GetProps<typeof InputFrame>;
  validator?: (a: unknown) => string | undefined;
  errorText?: string;
  readonly?: boolean;
  loading?: boolean;
} & Omit<GetProps<typeof TamaguiInput>, "prefix" | "postfix">;

type TextAreaProps = Omit<InputProps, "prefix" | "postfix">;
type MaskedInputType = Omit<InputProps, "mask" | "onChangeText"> &
  UseMaskedInputProps;

const InputContentWrapper = ({
  multiline,
  prefix,
  postfix,
  children,
}: {
  multiline?: boolean;
  prefix?: React.ReactNode;
  postfix?: React.ReactNode;
  children: React.ReactElement;
}) =>
  multiline ? (
    children
  ) : (
    <XStack gap="$100" alignItems="center">
      {prefix}
      {children}
      {postfix}
    </XStack>
  );

const ControlledInput = React.forwardRef<TextInput, InputProps>(
  (
    {
      errorText,
      id,
      label,
      onBlur,
      onFocus,
      rootProps,
      frameProps,
      value,
      prefix,
      postfix,
      mode = "bordered",
      inputMode,
      multiline,
      readonly,
      loading,
      ...props
    },
    ref,
  ) => {
    const [focused, setFocused] = React.useState(false);
    const { fieldId: inputId, labelId, errorId } = useFormFieldIds(id);
    const formFieldAria = useFormFieldAria({
      hasLabel: Boolean(label),
      labelId,
      errorId,
      errorText,
    });
    // tamagui v2 removed multiline prop, we retain support
    const InputEl = (
      multiline ? TamaguiTextArea : TamaguiInput
    ) as typeof TamaguiInput; // TextArea is identical to Input but with render:"textarea", we cast to Input simplify TS types
    return (
      <FormField
        fieldId={inputId}
        label={label}
        errorText={errorText}
        rootProps={rootProps}
        labelProps={
          Platform.OS === "web"
            ? undefined
            : {
                "aria-hidden": true, // Hides Label from native screen readers to prevent redundant announcement
              }
        }
        labelAccessory={
          readonly ? (
            <Lock
              strokeWidth={4}
              size="$150"
              color="$color"
              role="presentation"
            />
          ) : undefined
        }
      >
        <InputFrame
          focused={focused}
          error={!readonly && Boolean(errorText)} // Must come after focused prop
          marginBottom="$50"
          readonly={readonly}
          {...(multiline && { justifyContent: "flex-start" })}
          {...frameProps}
        >
          <InputContentWrapper
            multiline={multiline}
            prefix={prefix}
            postfix={postfix}
          >
            <InputEl
              id={inputId}
              {...formFieldAria}
              {...(Platform.OS !== "web" && {
                accessibilityLabel: label,
              })}
              accessibilityHint={errorText}
              ref={ref}
              fontFamily="$sans-serif"
              fontSize="$md"
              lineHeight="$md"
              paddingVertical="$0"
              paddingHorizontal="$50"
              unstyled
              value={value}
              focusStyle={{
                outlineStyle: "none",
              }}
              {...(multiline
                ? { multiline: true, width: "100%" as const }
                : { flexGrow: 1 as const })}
              onFocus={(e) => {
                if (onFocus) {
                  onFocus(e);
                }
                setFocused(true);
              }}
              onBlur={(e) => {
                if (onBlur) {
                  onBlur(e);
                }
                setFocused(false);
              }}
              inputMode={inputMode}
              enterKeyHint={inputMode === "numeric" ? "done" : undefined}
              readOnly={readonly}
              color={readonly ? "$textSecondary" : "$textPrimary"}
              placeholderTextColor="$placeholderColor"
              {...props}
            />
          </InputContentWrapper>
        </InputFrame>
      </FormField>
    );
  },
);
ControlledInput.displayName = "Input.Controlled";

const ControlledMaskedInput = React.forwardRef<TextInput, MaskedInputType>(
  (
    {
      value,
      mask,
      onChangeText,
      placeholderFillCharacter,
      obfuscationCharacter,
      maskAutoComplete = true,
      showObfuscatedValue,
      ...props
    },
    ref,
  ) => {
    const maskedInputProps = useMaskedInputProps({
      value,
      mask,
      onChangeText,
      placeholderFillCharacter,
      obfuscationCharacter,
      maskAutoComplete,
      showObfuscatedValue,
    });
    return <ControlledInput ref={ref} {...maskedInputProps} {...props} />;
  },
);
ControlledMaskedInput.displayName = "MaskedInput.Controlled";

type ValidatableProps = Pick<
  InputProps,
  "onBlur" | "validator" | "value" | "errorText"
>;

const withoutControl = <TProps extends ValidatableProps>(
  Component: React.ForwardRefExoticComponent<
    TProps & React.RefAttributes<TextInput>
  >,
  displayName: string,
) => {
  const WrappedComponent = React.forwardRef<TextInput, TProps>(
    ({ onBlur, validator, ...props }, ref) => {
      const [errorText, setErrorText] = React.useState<string | undefined>();
      const internalOnBlur: InputProps["onBlur"] = (e) => {
        onBlur?.(e);
        if (validator) {
          setErrorText(validator(props.value));
        }
      };
      return (
        <Component
          {...(props as unknown as TProps)}
          errorText={errorText}
          ref={ref}
          onBlur={internalOnBlur}
        />
      );
    },
  );
  WrappedComponent.displayName = displayName;
  return WrappedComponent;
};

const UncontrolledInput = withoutControl(ControlledInput, "Input");
const UncontrolledMaskedInput = withoutControl(
  ControlledMaskedInput,
  "MaskedInput",
);

export const Input = withStaticProperties(UncontrolledInput, {
  Controlled: ControlledInput,
}) as typeof UncontrolledInput & { Controlled: typeof ControlledInput };

/**
 * Event shape for an Input's `onKeyPress`. Tamagui v2 types it as an
 * intersection of a web KeyboardEvent and a minimal native event, so a
 * standalone (non-inline) handler must annotate with this. `preventDefault`
 * and `shiftKey` only exist on web, hence optional.
 */
export type InputKeyPressEvent = {
  nativeEvent: { key: string; shiftKey?: boolean; preventDefault?: () => void };
  preventDefault?: () => void;
};

export const MaskedInput = withStaticProperties(UncontrolledMaskedInput, {
  Controlled: ControlledMaskedInput,
}) as typeof UncontrolledMaskedInput & {
  Controlled: typeof ControlledMaskedInput;
};

const ControlledTextArea = styled(ControlledInput, {
  multiline: true,
}) as React.ForwardRefExoticComponent<
  TextAreaProps & React.RefAttributes<TextInput>
>;
ControlledTextArea.displayName = "TextArea.Controlled";

const UncontrolledTextArea = withoutControl(ControlledTextArea, "TextArea");

export const TextArea = withStaticProperties(UncontrolledTextArea, {
  Controlled: ControlledTextArea,
}) as typeof UncontrolledTextArea & { Controlled: typeof ControlledTextArea };
